import React, { useState } from 'react';
import { Button, Icon, Input, Popover, Tooltip } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { SeparateNumberFormat } from '../../../../../../services/utils';
import { impressions } from '../../../services/investmentCalculations';
import Colors from '../../../../../../themes/Colors';
import ImpressionsInput from './ImpressionsInput';

/**
 * Read-only playouts/impressions chip with an optional edit popover.
 * `multiplier` picks the unit: 1 edits playouts, the display multiplier edits
 * impressions. Either way `onApply` receives promised_shows.
 *
 * @param {number}   promisedShows
 * @param {number}   multiplier
 * @param {string}   warningKey - i18n key rendered with <b> as <strong>
 * @param {function} onApply    - (promisedShows: number) => void
 */
const EditableSpotsIndicator = ({
  label,
  promisedShows,
  multiplier = 1,
  icon = 'play-circle',
  extra = null,
  editable = false,
  disabled = false,
  editTitle,
  warningKey,
  daysTooltip,
  max,
  onApply
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [draft, setDraft] = useState(promisedShows);

  const handleVisibleChange = nextVisible => {
    // A disabled button doesn't stop the Popover trigger from catching the click.
    if (nextVisible && disabled) return;
    if (nextVisible) setDraft(promisedShows);
    setVisible(nextVisible);
  };

  return (
    <div className="totalImpactsContainer">
      <div className="textGroup" style={{ textAlign: 'left', margin: 0 }}>
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '5px',
          marginBottom: '5px'
        }}
      >
        <div className="screenCardTotalImpacts" size="default">
          <Icon type={icon} theme="outlined" />
          <span>{SeparateNumberFormat(impressions.fromSpots(promisedShows, multiplier))}</span>
        </div>
        {extra}
        {editable && (
          <Popover
            trigger="click"
            placement="right"
            title={editTitle}
            visible={visible && !disabled}
            onVisibleChange={handleVisibleChange}
            content={
              <div style={{ maxWidth: '320px' }}>
                <div className="textGroup" style={{ margin: 0, textAlign: 'left' }}>
                  <Trans i18nKey={warningKey} components={{ b: <strong /> }} />
                </div>
                <Input.Group compact style={{ display: 'flex', marginTop: '10px' }}>
                  <Tooltip placement="left" title={daysTooltip}>
                    <ImpressionsInput
                      promisedShows={draft}
                      multiplier={multiplier}
                      onChangePromisedShows={setDraft}
                      max={max}
                      style={{ flex: 1, width: '100%' }}
                    />
                  </Tooltip>
                  <Button
                    type="primary"
                    onClick={() => {
                      onApply(draft);
                      setVisible(false);
                    }}
                  >
                    {t('Assign')}
                  </Button>
                </Input.Group>
              </div>
            }
          >
            <Button className="buttonActionsGroup" size='small' icon="edit" shape="circle" disabled={disabled}/>
          </Popover>
        )}
      </div>
    </div>
  );
};

export default EditableSpotsIndicator;
